import { useProfile } from 'hooks/auth';
import { useGetShiftMeActive } from 'hooks/shifts';
import { useCallback } from 'react';
import { useDispatch } from 'react-redux';
import {
  updateMessageOmniChat,
  useCreateChatMessengerMutation,
  useHideCommentMutation,
  useLikeCommentMutation,
  useRemoveCommentMutation
} from 'services';
import { useCreateContactEventMutation } from 'services/tickets';
import { AppDispatch } from 'store';
import { ChatSessionDto, OmniChannelMessageDto } from 'types';
export function useActionsOmniChatMessage(
  chatId: number,
  chatSessionId: number,
  endpointNameMessages?: string,
  setChatSession?: (value: ChatSessionDto) => void
) {
  const dispatch = useDispatch<AppDispatch>();

  const [onLikeComment] = useLikeCommentMutation();
  const [onRemoveComment] = useRemoveCommentMutation();
  const [onHideComment] = useHideCommentMutation();
  const [onCreateChat] = useCreateChatMessengerMutation();
  const { profile } = useProfile();
  const { shiftMeActive } = useGetShiftMeActive();
  const [onCreateContactEvent] = useCreateContactEventMutation();

  const saveContactResponseToSession = (chatSessionId: number, contactResponse: any) => {
    try {
      // Extract only the data field from the response
      const dataField = contactResponse.data;

      const storedSessions = localStorage.getItem('customerSupportSession');
      if (!storedSessions) {
        // If no sessions exist yet, create a new one with only the data field
        const newSession = {
          chatSessionId,
          searchData: [],
          type: 'chat',
          contactResponse: dataField // Store only the data field
        };
        localStorage.setItem('customerSupportSession', JSON.stringify([newSession]));

        return;
      }

      const sessions = JSON.parse(storedSessions);
      // Find the session for this chat
      const sessionIndex = sessions.findIndex((s: any) => s.chatSessionId === chatSessionId);

      if (sessionIndex >= 0) {
        // Update existing session with only data field
        sessions[sessionIndex].contactResponse = dataField;
      } else {
        // Create new session with only data field
        sessions.push({
          chatSessionId,
          searchData: [],
          type: 'chat',
          contactResponse: dataField
        });
      }

      // Save back to localStorage
      localStorage.setItem('customerSupportSession', JSON.stringify(sessions));
    } catch (error) {
      console.error('❌ Error saving contact response to session:', error);
    }
  };
  const dispatchUpdateMessage = useCallback(
    (data: OmniChannelMessageDto, messageId: number) => {
      if (endpointNameMessages)
        dispatch(updateMessageOmniChat(data, messageId, endpointNameMessages, { chatId, chatSessionId }));
    },
    [endpointNameMessages]
  );

  const handleLikeMessage = (message: OmniChannelMessageDto) => {
    onLikeComment(message.messageId)
      .unwrap()
      .finally(() => {
        dispatchUpdateMessage(
          {
            ...message,
            isLiked: true
          },
          message.messageId
        );
      });
  };

  const handleHideMessage = (message: OmniChannelMessageDto) => {
    onHideComment({
      messageId: message.messageId,
      isHidden: !message.isHidden
    })
      .unwrap()
      .then(() => {
        dispatchUpdateMessage(
          {
            ...message,
            isHidden: !message.isHidden
          },
          message.messageId
        );
      });
  };

  const handleDeleteMessage = (message: OmniChannelMessageDto) => {
    onRemoveComment(message.messageId)
      .unwrap()
      .then(() => {
        dispatchUpdateMessage(
          {
            ...message,
            isDeleted: true
          },
          message.messageId
        );
      });
  };

  const handleChatUser = (message: OmniChannelMessageDto) => {
    onCreateChat(message.chatSessionId)
      .unwrap()
      .then((rs) => {
        setChatSession?.(rs.data);
        if (profile?.extensionId && profile?.username && shiftMeActive?.shiftId) {
          onCreateContactEvent({
            agentId: profile?.userId,
            agentName: profile?.username,
            channelId: 14,
            contactAccount: rs.data.chat?.customerId,
            contactId: rs.data.chatSessionId.toString(),
            flowCode: 'Chat',
            shiftId: shiftMeActive?.shiftId,
            chatSessionId: rs.data?.chatSessionId
          })
            .catch(error => {
              console.error('❌ Failed to create contact event:', error);
            });
        }
      });
  };

  const handleUpdateMessage = (message: OmniChannelMessageDto) => {
    dispatchUpdateMessage(
      {
        ...message
      },
      message.messageId
    );
  };

  return { handleUpdateMessage, handleLikeMessage, handleHideMessage, handleDeleteMessage, handleChatUser };
}
